import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'next-i18next/pages'
import { SavedListItem } from 'pcl-ui'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { track } from 'track'
import { localSavedStatesStore } from '@/state/localSavedStatesStore'
import {
  clearMetaState,
  ListType,
  replaceMetaState,
  replaceOptionsState,
  replaceReasonsState,
} from '@/state/reducers'
import { getCollectionStats, getListStats } from '@/state/stateStats'
import { RootState, store } from '@/state/store'
import { confirmUnsavedChanges } from '@/utils/compareState'

function getInitialsFromFileName(fileName: string) {
  const words = fileName.split(' ')
  const ignoreWords = ['the', 'a', 'an', 'to', 'for', 'in', 'of', 'on']
  let initialsArray = words
    .filter((word) => !ignoreWords.includes(word.toLowerCase()))
    .map((word) => word.charAt(0).toUpperCase())
  if (initialsArray.length < 2 && words.length >= 2) {
    initialsArray = words
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
  }
  return initialsArray.join('').slice(0, 2)
}

async function retrieveAllItems(): Promise<ListType[]> {
  try {
    const keys = await localSavedStatesStore.keys()
    const items = await Promise.all(
      keys.map((key) => localSavedStatesStore.getItem<ListType>(key)),
    )
    const filteredList = items
      .map((item) => {
        if (!item?.meta.fileName) {
          console.error('Failed to retrieve properly formed listItem', item)
          if (item?.meta.key) localSavedStatesStore.removeItem(item.meta.key)
          throw new Error('Failed to retrieve properly formed listItem')
        }
        return {
          ...item,
          meta: {
            ...item.meta,
            initials: getInitialsFromFileName(item.meta.fileName),
          },
        }
      })
      .filter((item) => item.meta.fileName)
    track('event', 'retrieve-all-items', getCollectionStats(filteredList))
    return filteredList
  } catch {
    return []
  }
}

export function SaveList({ handleClose }: { handleClose: () => void }) {
  const [lists, setLists] = useState<ListType[]>([])
  const [renamingKey, setRenamingKey] = useState<string | null>(null)
  const { i18n, t } = useTranslation('modals')
  const dispatch = useDispatch()
  const askBeforeDiscarding = useSelector(
    (state: RootState) => state.settings.askBeforeDiscardingUnsavedChanges,
  )

  useEffect(() => {
    retrieveAllItems().then((items) => setLists(items.filter(Boolean)))
  }, [])

  async function handleDeleteClick(e: React.MouseEvent, item: ListType) {
    e.stopPropagation()
    const confirmed = window.confirm(
      t('SaveList.AreYouSureYouWantToDeleteThisList'),
    )
    if (!confirmed) return
    try {
      if (item.meta.key) {
        await localSavedStatesStore.removeItem(item.meta.key)
        const items = await retrieveAllItems()
        setLists(items.filter(Boolean))
        if (store.getState().meta.key === item.meta.key) {
          dispatch(replaceOptionsState([]))
          dispatch(clearMetaState())
        }
      }
    } catch (error) {
      console.error('Failed to delete item:', error)
    }
  }

  async function handleRenameConfirm(item: ListType, newName: string) {
    setRenamingKey(null)
    if (!newName || !item.meta.key) return
    try {
      const stored = await localSavedStatesStore.getItem<ListType>(
        item.meta.key,
      )
      if (stored?.meta.key) {
        stored.meta.fileName = newName
        await localSavedStatesStore.setItem(stored.meta.key, stored)
        dispatch(replaceMetaState(stored.meta))
        setLists(await retrieveAllItems())
      }
    } catch (error) {
      console.error('Failed to rename item:', error)
    }
  }

  function handleLoad(savedState: ListType) {
    confirmUnsavedChanges(
      store.getState(),
      t(
        'common:AppControls.YouHaveUnsavedChangesDoYouWantToContinueAndDiscardChanges',
      ),
      'load-list',
      askBeforeDiscarding,
    ).then((ok) => {
      if (ok) {
        dispatch(replaceOptionsState(savedState.options))
        dispatch(replaceReasonsState(savedState.reasons))
        dispatch(replaceMetaState(savedState.meta))
        handleClose()
        track('event', 'load-list', getListStats(savedState))
      }
    })
  }

  function formatDate(ts: number | undefined) {
    if (!ts) return undefined
    return new Intl.DateTimeFormat(
      i18n.resolvedLanguage ?? i18n.language,
    ).format(new Date(ts))
  }

  return (
    <div data-test="load-drawer">
      {/* Info note */}
      <div className="flex gap-2 items-start mb-4 px-3 py-2.5 bg-[#e8f4fd] dark:bg-[#0d1e2a] border-2 border-[#90caf9] dark:border-[#1a3a52] rounded-lg shadow-[2px_2px_0_#5b9fd4] dark:shadow-[2px_2px_0_#000]">
        <InformationCircleIcon className="h-3.5 w-3.5 text-[#1976d2] dark:text-[#5b9fd4] mt-0.5 shrink-0" />
        <p className="text-[11px] font-extrabold tracking-[0.05em] uppercase leading-relaxed text-[#1565c0] dark:text-[#5b9fd4]">
          {t('SaveList.ListsAreOnlySavedLocallyInYourBrowser')}
        </p>
      </div>

      {/* List */}
      <ul role="list" className="flex flex-col gap-2">
        {lists.map((list) => (
          <li key={list.meta.key ?? list.meta.fileName}>
            <SavedListItem
              title={list.meta.fileName ?? ''}
              initials={list.meta.initials ?? '??'}
              colorClass={list.meta.color ?? 'bg-ink'}
              formattedDate={formatDate(
                list.meta.updatedAt ?? list.meta.savedAt,
              )}
              lastUpdatedLabel={t('SaveList.LastUpdated')}
              isRenaming={renamingKey === list.meta.key}
              onLoad={() => handleLoad(list)}
              onEditClick={(e) => {
                e.stopPropagation()
                setRenamingKey(list.meta.key ?? null)
              }}
              onDeleteClick={(e) => handleDeleteClick(e, list)}
              onRenameConfirm={(name) => handleRenameConfirm(list, name)}
              onRenameCancel={() => setRenamingKey(null)}
              editLabel={t('SaveList.editList')}
              deleteLabel={t('SaveList.deleteList')}
            />
          </li>
        ))}

        {lists.length === 0 && (
          <li>
            <div className="mt-2 py-7 px-5 text-center border-2 border-dashed border-ink/20 dark:border-dark-border/40 rounded-[10px]">
              <p className="text-sm font-bold text-ink-secondary dark:text-dark-ink2">
                {t('SaveList.noSavedLists')}
              </p>
              <p className="text-xs font-semibold text-ink-secondary/60 dark:text-dark-ink2/60 mt-1">
                {t('SaveList.saveAListToSeeItHere')}
              </p>
            </div>
          </li>
        )}
      </ul>
    </div>
  )
}
